import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { ChevronRight, Heart, Sparkles, Gift } from 'lucide-react'
import { InnerHero } from '@/components/sections'
import { Prefooter, Footer } from '@/components/layout'
import { LocalizedLink } from '@/components/LocalizedLink'

type Step = { n: string; titleKey: string; bodyKey: string }

function getHowItWorksSteps(t: (k: string) => string): Step[] {
  return [
    {
      n: '01',
      titleKey: t('loversClub.step1Title'),
      bodyKey: t('loversClub.step1Body'),
    },
    {
      n: '02',
      titleKey: t('loversClub.step2Title'),
      bodyKey: t('loversClub.step2Body'),
    },
    {
      n: '03',
      titleKey: t('loversClub.step3Title'),
      bodyKey: t('loversClub.step3Body'),
    },
  ]
}

type Welcome = { points: string; labelKey: string; bodyKey: string }

function getWelcomeBonus(t: (k: string) => string): Welcome[] {
  return [
    {
      points: '+50',
      labelKey: t('loversClub.bonus1Label'),
      bodyKey: t('loversClub.bonus1Body'),
    },
    {
      points: '+50',
      labelKey: t('loversClub.bonus2Label'),
      bodyKey: t('loversClub.bonus2Body'),
    },
  ]
}

type Tier = {
  id: string
  nameKey: string
  range: string
  thresholdKey: string
  descriptionKey: string
  benefitKeys: string[]
  birthdayKey: string
}

function getTiers(t: (k: string) => string): Tier[] {
  return [
    {
      id: 'nashilover',
      nameKey: t('loversClub.tier1Name'),
      range: '0 — 249',
      thresholdKey: t('loversClub.tier1Threshold'),
      descriptionKey: t('loversClub.tier1Desc'),
      benefitKeys: [
        t('loversClub.tier1Benefit1'),
        t('loversClub.tier1Benefit2'),
        t('loversClub.tier1Benefit3'),
        t('loversClub.tier1Benefit4'),
      ],
      birthdayKey: t('loversClub.tier1Birthday'),
    },
    {
      id: 'nashiaddicted',
      nameKey: t('loversClub.tier2Name'),
      range: '250 — 499',
      thresholdKey: t('loversClub.tier2Threshold'),
      descriptionKey: t('loversClub.tier2Desc'),
      benefitKeys: [
        t('loversClub.tier2Benefit1'),
        t('loversClub.tier2Benefit2'),
        t('loversClub.tier2Benefit3'),
        t('loversClub.tier2Benefit4'),
        t('loversClub.tier2Benefit5'),
      ],
      birthdayKey: t('loversClub.tier2Birthday'),
    },
    {
      id: 'nashivip',
      nameKey: t('loversClub.tier3Name'),
      range: '500+',
      thresholdKey: t('loversClub.tier3Threshold'),
      descriptionKey: t('loversClub.tier3Desc'),
      benefitKeys: [
        t('loversClub.tier3Benefit1'),
        t('loversClub.tier3Benefit2'),
        t('loversClub.tier3Benefit3'),
        t('loversClub.tier3Benefit4'),
        t('loversClub.tier3Benefit5'),
        t('loversClub.tier3Benefit6'),
      ],
      birthdayKey: t('loversClub.tier3Birthday'),
    },
  ]
}

function getPerks(t: (k: string) => string) {
  return [
    { ic: '01', titleKey: t('loversClub.perk1Title'), bodyKey: t('loversClub.perk1Body') },
    { ic: '02', titleKey: t('loversClub.perk2Title'), bodyKey: t('loversClub.perk2Body') },
    { ic: '03', titleKey: t('loversClub.perk3Title'), bodyKey: t('loversClub.perk3Body') },
    { ic: '04', titleKey: t('loversClub.perk4Title'), bodyKey: t('loversClub.perk4Body') },
    { ic: '05', titleKey: t('loversClub.perk5Title'), bodyKey: t('loversClub.perk5Body') },
    { ic: '06', titleKey: t('loversClub.perk6Title'), bodyKey: t('loversClub.perk6Body') },
  ]
}

export function NashiLoversClubPage() {
  const { t } = useTranslation('common')

  const howItWorksSteps = getHowItWorksSteps(t)
  const welcomeBonus = getWelcomeBonus(t)
  const tiers = getTiers(t)
  const perks = getPerks(t)

  return (
    <main className="bg-cream">
      <InnerHero
        eyebrow={t('loversClub.heroEyebrow')}
        title={t('loversClub.heroTitle')}
        titleItalic={t('loversClub.heroTitleItalic')}
        body={t('loversClub.heroBody')}
        breadcrumbs={[
          { label: t('nav.home'), href: '/it' },
          { label: t('nav.nashiLoversClub') },
        ]}
      />

      {/* Manifesto */}
      <section className="relative bg-cream py-16 md:py-24 border-b border-bruno/10 overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-12 right-[-3%] select-none font-display italic text-argan/[0.05]"
          style={{ fontSize: 'clamp(10rem, 22vw, 22rem)', lineHeight: 0.85 }}
        >
          ♥
        </div>
        <div className="relative nashi-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.9 }}
              className="lg:col-span-5"
            >
              <span className="nashi-eyebrow">{t('loversClub.manifestoEyebrow')}</span>
              <h2
                className="mt-6 font-display text-bruno"
                style={{
                  fontSize: 'clamp(2rem, 3.6vw, 3rem)',
                  lineHeight: 1,
                  letterSpacing: '-0.015em',
                }}
              >
                <span className="block">{t('loversClub.manifestoTitle1')}</span>
                <span className="block italic font-light text-argan-deep">
                  {t('loversClub.manifestoTitle2')}
                </span>
              </h2>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.9, delay: 0.15 }}
              className="lg:col-span-7 lg:pt-6"
            >
              <p className="text-bruno-soft text-base md:text-lg leading-[1.75]">
                {t('loversClub.manifestoBody1')}
              </p>
              <p className="mt-5 text-bruno-soft text-base md:text-lg leading-[1.75]">
                {t('loversClub.manifestoBody2')}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Come funziona — 3 step */}
      <section className="relative bg-cream-soft py-20 md:py-28 overflow-hidden border-b border-bruno/10">
        <div className="nashi-container">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.9 }}
            className="max-w-2xl mb-16 md:mb-20"
          >
            <span className="nashi-eyebrow">{t('loversClub.howItWorksEyebrow')}</span>
            <h2
              className="mt-6 font-display text-bruno"
              style={{
                fontSize: 'clamp(2rem, 3.6vw, 3rem)',
                lineHeight: 1,
                letterSpacing: '-0.015em',
              }}
            >
              <span className="block">{t('loversClub.howItWorksTitle1')}</span>
              <span className="block italic font-light text-argan-deep">
                {t('loversClub.howItWorksTitle2')}
              </span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-bruno/10 border border-bruno/10">
            {howItWorksSteps.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.9, delay: i * 0.1 }}
                className="bg-cream-soft p-8 md:p-10 flex flex-col gap-6 group hover:bg-cream transition-colors duration-700"
              >
                <div className="flex items-baseline justify-between">
                  <span className="font-display italic text-argan-deep text-5xl md:text-6xl tabular-nums">
                    {s.n}
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.32em] text-bruno-muted">
                    {t('loversClub.stepLabel')}
                  </span>
                </div>
                <h3
                  className="font-display text-bruno"
                  style={{
                    fontSize: 'clamp(1.25rem, 1.8vw, 1.625rem)',
                    lineHeight: 1.15,
                    letterSpacing: '-0.005em',
                  }}
                >
                  {s.titleKey}
                </h3>
                <p className="text-bruno-soft text-[15px] leading-[1.75]">{s.bodyKey}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Welcome bonus */}
      <section className="relative bg-cream py-20 md:py-28 overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute bottom-[-10%] left-[-5%] select-none font-display italic text-argan/[0.06]"
          style={{ fontSize: 'clamp(12rem, 24vw, 24rem)', lineHeight: 0.85 }}
        >
          +
        </div>
        <div className="relative nashi-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.9 }}
              className="lg:col-span-4"
            >
              <span className="nashi-eyebrow">{t('loversClub.bonusEyebrow')}</span>
              <h2
                className="mt-6 font-display text-bruno"
                style={{
                  fontSize: 'clamp(1.75rem, 3vw, 2.5rem)',
                  lineHeight: 1.05,
                  letterSpacing: '-0.015em',
                }}
              >
                <span className="block">{t('loversClub.bonusTitle1')}</span>
                <span className="block italic font-light text-argan-deep">{t('loversClub.bonusTitle2')}</span>
              </h2>
              <p className="mt-6 text-bruno-soft text-[15px] leading-[1.75] max-w-sm">
                {t('loversClub.bonusBody')}
              </p>
            </motion.div>

            <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {welcomeBonus.map((w, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.9, delay: i * 0.1 }}
                  className="relative border border-bruno/10 bg-cream-soft p-8 md:p-10 hover:border-argan/40 transition-colors duration-700"
                >
                  <span className="absolute top-6 right-6 text-[10px] uppercase tracking-[0.32em] text-bruno-muted">
                    {String(i + 1).padStart(2, '0')}.
                  </span>
                  <p
                    className="font-display italic text-argan-deep tabular-nums"
                    style={{ fontSize: 'clamp(3rem, 6vw, 5.5rem)', lineHeight: 0.9 }}
                  >
                    {w.points}
                  </p>
                  <p className="mt-2 text-[10px] uppercase tracking-[0.32em] text-bruno-muted">
                    {t('loversClub.pointsLabel')}
                  </p>
                  <h3 className="mt-6 font-display text-bruno text-xl md:text-2xl">
                    {w.labelKey}
                  </h3>
                  <p className="mt-4 text-bruno-soft text-[14px] md:text-[15px] leading-[1.75]">
                    {w.bodyKey}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Tiers ladder */}
      <section
        aria-label={t('loversClub.tiersAriaLabel')}
        className="relative bg-cream-soft py-20 md:py-28 overflow-hidden border-y border-bruno/10"
      >
        <div className="nashi-container">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.9 }}
            className="max-w-3xl mb-16 md:mb-20"
          >
            <span className="nashi-eyebrow">{t('loversClub.tiersEyebrow')}</span>
            <h2
              className="mt-6 font-display text-bruno"
              style={{
                fontSize: 'clamp(2rem, 3.8vw, 3.25rem)',
                lineHeight: 1,
                letterSpacing: '-0.015em',
              }}
            >
              <span className="block">{t('loversClub.tiersTitle1')}</span>
              <span className="block italic font-light text-argan-deep">
                {t('loversClub.tiersTitle2')}
              </span>
            </h2>
            <p className="mt-8 text-bruno-soft text-base md:text-lg leading-[1.75]">
              {t('loversClub.tiersBody')}
            </p>
          </motion.div>

          {/* Stat header */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 mb-16 md:mb-20 pb-10 border-b border-bruno/10">
            <div>
              <p className="text-[10px] uppercase tracking-[0.32em] text-bruno-muted">
                {t('loversClub.statConversion')}
              </p>
              <p
                className="mt-3 font-display italic text-argan-deep tabular-nums"
                style={{ fontSize: 'clamp(2rem, 3.6vw, 3rem)', lineHeight: 1 }}
              >
                1 € = 1 pt
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.32em] text-bruno-muted">
                {t('loversClub.statCoupon')}
              </p>
              <p
                className="mt-3 font-display italic text-argan-deep tabular-nums"
                style={{ fontSize: 'clamp(2rem, 3.6vw, 3rem)', lineHeight: 1 }}
              >
                10%
              </p>
              <p className="mt-2 text-xs text-bruno-muted">{t('loversClub.statCouponNote')}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.32em] text-bruno-muted">
                {t('loversClub.statValidity')}
              </p>
              <p
                className="mt-3 font-display italic text-argan-deep tabular-nums"
                style={{ fontSize: 'clamp(2rem, 3.6vw, 3rem)', lineHeight: 1 }}
              >
                60 gg
              </p>
              <p className="mt-2 text-xs text-bruno-muted">
                {t('loversClub.statValidityNote')}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.32em] text-bruno-muted">
                {t('loversClub.statLevels')}
              </p>
              <p
                className="mt-3 font-display italic text-argan-deep tabular-nums"
                style={{ fontSize: 'clamp(2rem, 3.6vw, 3rem)', lineHeight: 1 }}
              >
                03
              </p>
            </div>
          </div>

          {/* Tier cards stack */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-bruno/10 border border-bruno/10">
            {tiers.map((tier, i) => (
              <motion.article
                key={tier.id}
                id={tier.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 1, delay: i * 0.12 }}
                className={[
                  'relative p-8 md:p-10 flex flex-col gap-7',
                  i === 2 ? 'bg-bruno text-cream' : 'bg-cream',
                ].join(' ')}
              >
                {/* Tier badge */}
                <div className="flex items-baseline justify-between gap-4">
                  <span
                    className={[
                      'text-[10px] uppercase tracking-[0.32em]',
                      i === 2 ? 'text-cream/60' : 'text-bruno-muted',
                    ].join(' ')}
                  >
                    {t('loversClub.levelLabel')} {String(i + 1).padStart(2, '0')}
                  </span>
                  {i === 2 && (
                    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.28em] text-argan">
                      <Sparkles className="h-3 w-3" strokeWidth={1.5} />
                      {t('loversClub.topTier')}
                    </span>
                  )}
                </div>

                <h3
                  className={[
                    'font-display',
                    i === 2 ? 'text-cream' : 'text-bruno',
                  ].join(' ')}
                  style={{
                    fontSize: 'clamp(1.875rem, 3vw, 2.5rem)',
                    lineHeight: 1,
                    letterSpacing: '-0.015em',
                  }}
                >
                  {tier.nameKey}
                </h3>

                {/* Range */}
                <div
                  className={[
                    'pb-5 border-b',
                    i === 2 ? 'border-cream/15' : 'border-bruno/10',
                  ].join(' ')}
                >
                  <p
                    className={[
                      'font-display italic tabular-nums',
                      i === 2 ? 'text-argan' : 'text-argan-deep',
                    ].join(' ')}
                    style={{ fontSize: 'clamp(2rem, 3.6vw, 3rem)', lineHeight: 1 }}
                  >
                    {tier.range}
                  </p>
                  <p
                    className={[
                      'mt-2 text-xs',
                      i === 2 ? 'text-cream/60' : 'text-bruno-muted',
                    ].join(' ')}
                  >
                    {tier.thresholdKey}
                  </p>
                </div>

                <p
                  className={[
                    'text-[14px] leading-[1.75]',
                    i === 2 ? 'text-cream/75' : 'text-bruno-soft',
                  ].join(' ')}
                >
                  {tier.descriptionKey}
                </p>

                {/* Benefits list */}
                <ul className="mt-2 space-y-2.5">
                  {tier.benefitKeys.map((b, j) => (
                    <li key={j} className="flex items-start gap-3">
                      <span
                        aria-hidden="true"
                        className={[
                          'mt-2 h-px w-3 shrink-0',
                          i === 2 ? 'bg-argan' : 'bg-argan',
                        ].join(' ')}
                      />
                      <span
                        className={[
                          'text-[14px] leading-snug',
                          i === 2 ? 'text-cream' : 'text-bruno',
                        ].join(' ')}
                      >
                        {b}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* Birthday highlight */}
                <div
                  className={[
                    'mt-auto pt-6 border-t flex items-baseline gap-3',
                    i === 2 ? 'border-cream/15' : 'border-bruno/10',
                  ].join(' ')}
                >
                  <Gift
                    className={[
                      'h-4 w-4 shrink-0',
                      i === 2 ? 'text-argan' : 'text-argan-deep',
                    ].join(' ')}
                    strokeWidth={1.5}
                  />
                  <span
                    className={[
                      'font-display italic',
                      i === 2 ? 'text-argan' : 'text-argan-deep',
                    ].join(' ')}
                    style={{ fontSize: 'clamp(1rem, 1.4vw, 1.25rem)' }}
                  >
                    {tier.birthdayKey}
                  </span>
                </div>
              </motion.article>
            ))}
          </div>

          {/* Footnote */}
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-10 text-xs italic text-bruno-muted leading-relaxed max-w-3xl"
          >
            {t('loversClub.tiersFootnote')}
          </motion.p>
        </div>
      </section>

      {/* Vantaggi extra (calendar of perks) */}
      <section className="relative bg-cream py-20 md:py-28 overflow-hidden">
        <div className="nashi-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.9 }}
              className="lg:col-span-4"
            >
              <span className="nashi-eyebrow">{t('loversClub.perksEyebrow')}</span>
              <h2
                className="mt-6 font-display text-bruno"
                style={{
                  fontSize: 'clamp(1.75rem, 3vw, 2.5rem)',
                  lineHeight: 1.05,
                  letterSpacing: '-0.015em',
                }}
              >
                <span className="block">{t('loversClub.perksTitle1')}</span>
                <span className="block italic font-light text-argan-deep">
                  {t('loversClub.perksTitle2')}
                </span>
              </h2>
              <p className="mt-6 text-bruno-soft text-[15px] leading-[1.75] max-w-sm">
                {t('loversClub.perksBody')}
              </p>
            </motion.div>

            <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-px bg-bruno/10 border border-bruno/10">
              {perks.map((perk, i) => (
                <motion.div
                  key={perk.ic}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.8, delay: i * 0.06 }}
                  className="bg-cream p-7 md:p-8 group hover:bg-cream-soft transition-colors duration-700"
                >
                  <div className="flex items-baseline justify-between mb-5">
                    <span className="font-display italic text-argan-deep tabular-nums text-2xl">
                      {perk.ic}
                    </span>
                    <Heart
                      className="h-3.5 w-3.5 text-argan/60 group-hover:text-argan transition-colors"
                      strokeWidth={1.5}
                    />
                  </div>
                  <h3 className="font-display text-bruno text-lg md:text-xl leading-tight">
                    {perk.titleKey}
                  </h3>
                  <p className="mt-3 text-bruno-soft text-[14px] leading-[1.7]">
                    {perk.bodyKey}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Coupon disclosure */}
      <section className="relative bg-cream-soft py-16 md:py-20 overflow-hidden border-y border-bruno/10">
        <div className="nashi-container">
          <div className="max-w-3xl mx-auto">
            <motion.aside
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.9 }}
              className="border-l-2 border-argan pl-8 py-2"
            >
              <p className="text-[10px] uppercase tracking-[0.32em] text-argan-deep mb-4">
                {t('loversClub.couponNoteTitle')}
              </p>
              <p className="text-bruno-soft text-[14px] md:text-[15px] leading-[1.85]">
                {t('loversClub.couponNoteBody')}
              </p>
            </motion.aside>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative bg-cream py-24 md:py-32 overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-24 right-[-3%] select-none font-display italic text-argan/[0.06]"
          style={{ fontSize: 'clamp(14rem, 28vw, 30rem)', lineHeight: 0.85 }}
        >
          ♥
        </div>
        <div className="relative nashi-container">
          <div className="max-w-3xl mx-auto text-center">
            <span className="nashi-eyebrow">{t('loversClub.ctaEyebrow')}</span>
            <h2
              className="mt-6 font-display text-bruno"
              style={{
                fontSize: 'clamp(2.25rem, 4.4vw, 3.75rem)',
                lineHeight: 1,
                letterSpacing: '-0.02em',
              }}
            >
              <span className="block">{t('loversClub.ctaTitle1')}</span>
              <span className="block italic font-light text-argan-deep">
                {t('loversClub.ctaTitle2')}
              </span>
            </h2>
            <p className="mt-8 text-bruno-soft text-base md:text-lg leading-relaxed max-w-xl mx-auto">
              {t('loversClub.ctaBody')}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://www.nashiargan.com/store-locator/"
                target="_blank"
                rel="noopener noreferrer"
                className="nashi-cta inline-flex items-center justify-center gap-3 bg-bruno text-cream text-[11px] uppercase tracking-[0.2em] px-8 py-4 hover:bg-argan-deep transition-colors duration-500"
              >
                {t('loversClub.ctaPrimary')}
                <ChevronRight className="h-3 w-3" strokeWidth={1.5} />
              </a>
              <LocalizedLink
                to="faq"
                className="inline-flex items-center justify-center gap-3 px-8 py-4 border border-bruno/30 text-bruno text-[11px] uppercase tracking-[0.2em] hover:border-argan hover:text-argan-deep transition-colors duration-500"
              >
                {t('loversClub.ctaSecondary')}
                <span aria-hidden="true">→</span>
              </LocalizedLink>
            </div>
          </div>
        </div>
      </section>

      <Prefooter />
      <Footer />
    </main>
  )
}
